import { useCallback, useEffect, useState } from "react";
import { PublicClientApplication } from "@azure/msal-browser";
import {
    fetchNotifications,
    markAllAsRead,
    markNotificationAsRead,
    Notification,
    NotificationError,
} from "@/services/notificationService";

export function useNotifications(
    applicationName: string,
    userEmail: string,
    msalInstance: PublicClientApplication | null
) {
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [loading, setLoading] = useState(false);

    const fetchAll = useCallback(async () => {
        if (!msalInstance) return;
        setLoading(true);
        try {
            if (msalInstance && userEmail && applicationName) {
                const data = await fetchNotifications(
                    applicationName,
                    userEmail,
                    msalInstance
                );
                setNotifications(
                    data.sort((a, b) => b.createdOn.localeCompare(a.createdOn))
                );
            }
        } catch (error) {
            if (
                error instanceof NotificationError &&
                /No active account found/i.test(error.message)
            ) {
                // Expected transient state during account rehydration in new tabs.
                setNotifications([]);
                return;
            }
            console.error("Failed to fetch notifications:", error);
            setNotifications([]);
        } finally {
            setLoading(false);
        }
    }, [applicationName, userEmail, msalInstance]);

    useEffect(() => {
        if (!userEmail || !msalInstance) return;
        fetchAll();
    }, [fetchAll, userEmail, msalInstance]);

    const handleMarkAllAsRead = useCallback(async () => {
        if (!msalInstance) return;
        await markAllAsRead(userEmail, msalInstance);
        fetchAll();
    }, [userEmail, fetchAll, msalInstance]);

    const markOneAsRead = useCallback(
        async (messageId: string | number) => {
            if (!msalInstance) return;

            setNotifications((prev) =>
                prev.map((n) =>
                    n.messageId === messageId
                        ? { ...n, readIndicator: true }
                        : n
                )
            );

            try {
                await markNotificationAsRead(
                    String(messageId),
                    userEmail,
                    msalInstance
                );
            } catch (error) {
                console.error(
                    "Failed to mark notification as read, reverting UI.",
                    error
                );
                fetchAll();
            }
        },
        [userEmail, fetchAll, msalInstance]
    );

    // Add a new notification to the local state immediately (optimistic update)
    const addNotification = useCallback((newNotification: Notification) => {
        setNotifications((prev) => {
            // Check if notification already exists to avoid duplicates
            const exists = prev.some(
                (n) => n.messageId === newNotification.messageId
            );
            if (exists) return prev;

            // Add new notification at the beginning (most recent)
            return [newNotification, ...prev];
        });
    }, []);

    return {
        notifications,
        loading,
        markAllAsRead: handleMarkAllAsRead,
        markOneAsRead,
        refetch: fetchAll,
        addNotification,
    };
}
